import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';
import Toast from '../../services/toast.service';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'toolkit-assessment-assign',
	templateUrl: './toolkit-assessment-assign.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe, LoaderOverlayComponent]
})
export default class ToolkitAssessmentAssignComponent implements OnInit {
	private static readonly TIMEPOINT_NOTES: Record<string, string> = {
		T1_BASELINE: 'Please complete this baseline EEK-2 before we begin tracking progress.',
		T2: 'This is the same questionnaire you filled at the start. It helps us see what has changed before session 4.',
		T3: 'Please fill in EEK-2 again before session 8 so we can review progress together.',
		AD_HOC: ''
	};

	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private route: ActivatedRoute = inject(ActivatedRoute);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private toast: Toast = inject(Toast);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.routingService.validateAccess('toolkit').then(() => this.onResolve());
	}

	onResolve() {
		this.vm.loading = true;
		this.vm.error = false;
		this.vm.step = 1; // 1=client, 2=details, 3=done

		this.vm.assessmentId = (this.route.snapshot.paramMap.get('id') || '').toString();

		this.vm.clientSearch = '';
		this.vm.clients = [];
		this.vm.filteredClients = [];
		this.vm.selectedClient = null;
		this.vm.preselectClientId = (this.route.snapshot.queryParamMap.get('clientId') || '').toString();
		if (!this.vm.preselectClientId) {
			this.vm.preselectClientId = (this.route.snapshot.queryParamMap.get('client_id') || '').toString();
		}
		this.vm.prefillTimepoint = (this.route.snapshot.queryParamMap.get('timepoint') || '').toString().toUpperCase();
		this.vm.prefillBookingId = Number(this.route.snapshot.queryParamMap.get('bookingId') || this.route.snapshot.queryParamMap.get('booking_id') || 0);
		this.vm.assignmentSource = (this.route.snapshot.queryParamMap.get('source') || 'manual').toString();

		this.vm.note = '';
		this.vm.dueAt = '';
		this.vm.isBaseline = true;
		this.vm.timepoint = 'T1_BASELINE';
		this.vm.sessionIndex = null;
		this.vm.bookingId = this.vm.prefillBookingId || null;

		this.vm.assessment = null;
		this.vm.questions = null;
		this.vm.scoring = null;
		this.vm.prior = [];
		this.vm.themes = [];

		this.vm.close = this.close.bind(this);
		this.vm.preview = this.preview.bind(this);
		this.vm.applyClientFilter = this.applyClientFilter.bind(this);
		this.vm.selectClient = this.selectClient.bind(this);
		this.vm.goStep = this.goStep.bind(this);
		this.vm.confirmAssignment = this.confirmAssignment.bind(this);
		this.vm.bandLabelFor = this.bandLabelFor.bind(this);
		this.vm.selectTimepoint = this.selectTimepoint.bind(this);
		this.vm.timepointLabel = this.timepointLabel.bind(this);
		this.vm.isEek2Assessment = this.isEek2Assessment.bind(this);
		this.vm.assessmentCardTitle = this.assessmentCardTitle.bind(this);
		this.vm.assessmentCardMeta = this.assessmentCardMeta.bind(this);
		this.vm.hasExistingBaseline = false;

		if (!this.vm.assessmentId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		this.toolkitService.getUserCustomers({}, (clients) => {
			this.vm.clients = Array.isArray(clients) ? clients : [];
			this.applyClientFilter();
			this.maybePreselectClient();
		});

		this.toolkitService.getCatalogue({}, (catalogue) => {
			this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
		});

		this.toolkitService.getAssessment(
			{ id: this.vm.assessmentId },
			(a) => {
				this.vm.assessment = a || null;
				this.vm.questions = parseJsonLenient(a?.questionsJson || a?.questions_json || null);
				this.vm.scoring = parseJsonLenient(a?.scoringJson || a?.scoring_json || null);
				if (!this.isEek2Assessment()) {
					this.vm.timepoint = 'AD_HOC';
					this.vm.isBaseline = false;
					this.applyTimepointDefaults(this.vm.selectedClient || null);
				}
				this.vm.loading = false;
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
			}
		);
	}

	private maybePreselectClient() {
		const raw = (this.vm?.preselectClientId || '').toString().trim();
		if (!raw) return;
		const idNum = Number(raw);
		const list: any[] = Array.isArray(this.vm?.clients) ? this.vm.clients : [];
		const found = list.find((c) => (c?.id != null ? Number(c.id) : NaN) === idNum) || null;
		if (!found) return;
		this.vm.preselectClientId = '';
		this.selectClient(found);
	}

	close() {
		this.angularRoutingHelperService.setPath('/toolkit', false, false);
	}

	preview() {
		this.angularRoutingHelperService.setPath(['/toolkit/assessment/preview', this.vm.assessmentId], false, false);
	}

	applyClientFilter() {
		const q = (this.vm.clientSearch || '').toString().trim().toLowerCase();
		const list = Array.isArray(this.vm.clients) ? this.vm.clients : [];
		this.vm.filteredClients = q
			? list.filter((c) => {
					const full = `${c?.firstname || ''} ${c?.lastname || ''}`.toLowerCase();
					const email = (c?.email || '').toLowerCase();
					return full.includes(q) || email.includes(q);
				})
			: list;
	}

	selectClient(c: any) {
		this.vm.selectedClient = c;
		this.vm.step = 2;
		this.loadPriorHistory();
	}

	goStep(step: number) {
		if (step === 2 && !this.vm.selectedClient) return;
		this.vm.step = step;
	}

	private loadPriorHistory() {
		const clientId = this.vm.selectedClient?.id;
		if (!clientId) return;
		this.vm.prior = [];
		this.toolkitService.getClientDetail(
			{ id: clientId },
			(detail) => {
				const list: any[] = Array.isArray(detail?.assessmentAssignments) ? detail.assessmentAssignments : [];
				const sameAssessment = list.filter((x) => x && x.assessmentId === this.vm.assessmentId && !x.isDeleted);
				const prior = sameAssessment
					.filter((x) => x && x.assessmentId === this.vm.assessmentId && x.status === 'completed' && !x.isDeleted)
					.sort((a, b) => {
						const ad = a?.completedAt ? new Date(a.completedAt).getTime() : 0;
						const bd = b?.completedAt ? new Date(b.completedAt).getTime() : 0;
						return ad - bd;
					});
				this.vm.prior = prior;
				this.vm.hasExistingBaseline = sameAssessment.some((x) => x?.timepoint === 'T1_BASELINE' || x?.isBaseline);
				this.vm.isBaseline = !this.vm.hasExistingBaseline;
				this.vm.timepoint = this.resolveInitialTimepoint();
				this.applyTimepointDefaults(detail?.client || this.vm.selectedClient || null);
			},
			() => {}
		);
	}

	private resolveInitialTimepoint(): string {
		if (!this.isEek2Assessment()) return 'AD_HOC';
		if (this.vm.prefillTimepoint && ['T1_BASELINE', 'T2', 'T3', 'AD_HOC'].includes(this.vm.prefillTimepoint)) {
			if (this.vm.prefillTimepoint === 'T1_BASELINE' && this.vm.hasExistingBaseline) {
				return 'AD_HOC';
			}
			return this.vm.prefillTimepoint;
		}
		if (this.vm.hasExistingBaseline) return 'AD_HOC';
		return 'T1_BASELINE';
	}

	selectTimepoint(timepoint: string) {
		if (!this.isEek2Assessment()) return;
		if (timepoint === 'T1_BASELINE' && this.vm.hasExistingBaseline) return;
		this.vm.timepoint = timepoint;
		this.vm.isBaseline = timepoint === 'T1_BASELINE';
		this.applyTimepointDefaults(this.vm.selectedClient || null);
	}

	timepointLabel(timepoint: string): string {
		if (timepoint === 'T1_BASELINE') return 'T1 Baseline';
		if (timepoint === 'T2') return 'T2 Session 4';
		if (timepoint === 'T3') return 'T3 Session 8';
		return 'Ad-hoc';
	}

	isEek2Assessment(): boolean {
		const values = [
			this.vm.assessmentId,
			this.vm.assessment?.id,
			this.vm.assessment?.assessmentId,
			this.vm.assessment?.code,
			this.vm.assessment?.title,
			this.vm.questions?.id,
			this.vm.questions?.code,
			this.vm.questions?.title
		];
		return values.some((value) => (value || '').toString().trim().toLowerCase() === 'eek2');
	}

	assessmentCardTitle(): string {
		const code = (this.vm.questions?.code || this.vm.assessment?.code || this.vm.assessment?.title || '').toString().trim();
		const fullTitle = (this.vm.questions?.fullTitle || this.vm.questions?.title || '').toString().trim();
		if (code && fullTitle && code !== fullTitle) return `${code} — ${fullTitle}`;
		return fullTitle || code;
	}

	assessmentCardMeta(): string {
		const parts: string[] = [];
		const themeName = this.themeName(this.vm.assessment?.themeId || this.vm.assessment?.theme_id || this.vm.assessment?.theme);
		const category = (themeName || this.vm.questions?.category || this.vm.assessment?.type || '').toString().trim();
		const itemCount = Number(this.vm.questions?.itemCount || (Array.isArray(this.vm.questions?.items) ? this.vm.questions.items.length : 0));
		const duration = Number(this.vm.assessment?.durationMinutes ?? this.vm.assessment?.duration_minutes ?? this.vm.questions?.estimatedMinutes ?? 0);

		if (category) parts.push(category);
		if (isFinite(itemCount) && itemCount > 0) parts.push(`${itemCount} questions`);
		if (isFinite(duration) && duration > 0) parts.push(`${duration} min`);
		return parts.join(' · ');
	}

	private themeName(themeId: any): string {
		const themes: any[] = Array.isArray(this.vm.themes) ? this.vm.themes : [];
		const found = themes.find((t) => (t?.id || '').toString() === (themeId || '').toString());
		return (found?.name || '').toString().trim();
	}

	private applyTimepointDefaults(client: any) {
		const timepoint = (this.vm.timepoint || 'AD_HOC').toString();
		const nextBooking = client?.nextBooking || null;
		this.vm.bookingId = this.vm.prefillBookingId || nextBooking?.bookingId || null;
		this.vm.sessionIndex = nextBooking?.sessionIndex || (timepoint === 'T2' ? 4 : timepoint === 'T3' ? 8 : timepoint === 'T1_BASELINE' ? 1 : null);
		this.vm.note = ToolkitAssessmentAssignComponent.TIMEPOINT_NOTES[timepoint] || '';
		if (timepoint === 'AD_HOC') {
			this.vm.dueAt = '';
			return;
		}
		const dueAt = this.isoDateMinusOneDay(nextBooking?.startsAt);
		this.vm.dueAt = dueAt || '';
	}

	private isoDateMinusOneDay(value: any): string {
		if (!value) return '';
		const d = new Date(value);
		if (!isFinite(d.getTime())) return '';
		d.setDate(d.getDate() - 1);
		const year = d.getFullYear();
		const month = `${d.getMonth() + 1}`.padStart(2, '0');
		const day = `${d.getDate()}`.padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	bandLabelFor(score: any): string {
		const s = Number(score);
		if (!isFinite(s)) return '—';
		const bands: any[] = Array.isArray(this.vm.scoring?.bands) ? this.vm.scoring.bands : [];
		for (const b of bands) {
			const min = Number(b?.min ?? Number.NEGATIVE_INFINITY);
			const max = Number(b?.max ?? Number.POSITIVE_INFINITY);
			if (s >= min && s <= max) {
				return (b?.label || b?.severity || '—').toString();
			}
		}
		return '—';
	}

	confirmAssignment() {
		if (!this.vm.selectedClient || !this.vm.assessmentId) return;
		if (this.isEek2Assessment() && this.vm.timepoint !== 'AD_HOC' && !this.vm.dueAt) {
			this.toast.show('Due date is required for T1, T2, and T3 assignments.');
			return;
		}
		this.vm.submitting = true;
		this.toolkitService.createAssessmentAssignment(
			{
				clientId: this.vm.selectedClient.id,
				assessmentId: this.vm.assessmentId,
				note: this.vm.note,
				dueAt: this.vm.dueAt || null,
				isBaseline: !!this.vm.isBaseline,
				timepoint: this.vm.timepoint,
				sessionIndex: this.vm.sessionIndex,
				bookingId: this.vm.bookingId,
				assignmentSource: this.vm.assignmentSource || 'manual'
			},
			() => {
				this.vm.submitting = false;
				this.vm.step = 3;
				this.toast.showWithTranslation('Toolkit.Assign.Success');
			},
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Assign.Error');
			}
		);
	}
}
